// Compose store — drives the chat-with-doc surface.
//
// Owns: the draft list, the active draft, that draft's chat thread, and
// the streaming turn. The AI participates in the document through the
// prompt-defined <edit> / <create_document> tag protocol
// (lib/doc-edit-stream.ts): prose streams to chat, an <edit> block
// replaces the active draft body, a <create_document> mints a new draft.
//
// Drafts persist to OPFS-SQLite immediately (survives reload) and back
// up to the private repo on a debounce (lib/drafts-backup.ts).

import { create } from "zustand";
import {
  listDrafts, getDraft, createDraft, updateDraft, deleteDraft,
  listMessages, addMessage, setPublished, clearPublished, slugTaken,
  type Draft, type ChatMessage,
} from "../lib/storage/drafts";
import { slugify, autoSummary } from "../lib/post";
import { publishAll } from "../lib/publish-flow";
import { pingRefresh } from "../lib/indexer-client";
import { useAuthStore } from "./authStore";
import { getOnboarding } from "../lib/storage/onboarding";
import { getSecret } from "../lib/secrets";
import { useKeyStore } from "./keyStore";
import { streamChat, type ChatTurnMessage } from "../lib/chat-send";
import { createEditStreamFilter, EDIT_PROTOCOL_PROMPT } from "../lib/doc-edit-stream";
import { scheduleBackup, flushBackup, backupDrafts } from "../lib/drafts-backup";

type BackupState = "idle" | "saving" | "saved" | "error";
export type PublishState = "idle" | "publishing" | "building" | "live" | "error";

interface ComposeState {
  sub: string | null;
  drafts: Draft[];
  activeId: string | null;
  messages: ChatMessage[];
  loading: boolean;
  /** A draft the Library tab asked to open; honored by init() if Compose
   *  hasn't mounted yet, applied immediately if it has. */
  requestedId: string | null;
  streaming: boolean;
  streamingText: string;
  aiEditing: boolean;
  error: string | null;
  backup: BackupState;
  publishState: PublishState;
  publishError: string | null;
  publishedUrl: string | null;

  init: (googleSub: string) => Promise<void>;
  newDraft: () => Promise<void>;
  selectDraft: (id: string) => Promise<void>;
  openDraft: (id: string) => void;
  setTitle: (title: string) => void;
  setBody: (body: string) => void;
  removeDraft: (id: string) => Promise<void>;
  insertIntoBody: (snippet: string) => void;
  publishActive: () => Promise<void>;
  unpublish: (id: string) => Promise<void>;
  send: (userText: string) => Promise<void>;
  backupNow: () => Promise<void>;
  clearError: () => void;
}

function buildSystemPrompt(draft: Draft): string {
  return `You are a warm, sharp writing assistant inside BKA, a tool a creator uses to publish posts to their own website. Help them draft. Keep chat replies short — do the real work in the document.

The creator's current draft is below (markdown).

--- CURRENT DRAFT: "${draft.title || "Untitled"}" ---
${draft.body || "(the draft is empty)"}
--- END DRAFT ---

${EDIT_PROTOCOL_PROMPT}`;
}

/** Pull the freshest auth + repo for a backup push. Returns null if the
 *  creator can't back up yet (no token / no public-repo onboarding). */
async function backupContext(sub: string, drafts: Draft[]) {
  const token = getSecret("github_token", sub);
  const onb = await getOnboarding(sub);
  if (!token || !onb?.private_repo) return null;
  return { token, privateRepo: onb.private_repo, drafts };
}

export const useComposeStore = create<ComposeState>((set, get) => ({
  sub: null,
  drafts: [],
  activeId: null,
  messages: [],
  loading: true,
  requestedId: null,
  streaming: false,
  streamingText: "",
  aiEditing: false,
  error: null,
  backup: "idle",
  publishState: "idle",
  publishError: null,
  publishedUrl: null,

  init: async (googleSub) => {
    set({ loading: true, sub: googleSub });
    useKeyStore.getState().hydrate(googleSub);
    let drafts = await listDrafts(googleSub);
    if (drafts.length === 0) {
      await createDraft(googleSub, { title: "", body: "" });
      drafts = await listDrafts(googleSub);
    }
    // Honor a draft the Library tab requested, if it's still present.
    const reqId = get().requestedId;
    const activeId = (reqId && drafts.some((d) => d.id === reqId)) ? reqId : drafts[0].id;
    const messages = await listMessages(activeId);
    set({ drafts, activeId, messages, loading: false, requestedId: null });
  },

  newDraft: async () => {
    const sub = get().sub;
    if (!sub) return;
    const d = await createDraft(sub, { title: "", body: "" });
    const drafts = await listDrafts(sub);
    set({ drafts, activeId: d.id, messages: [], streamingText: "", error: null });
  },

  selectDraft: async (id) => {
    const messages = await listMessages(id);
    set({ activeId: id, messages, streamingText: "", error: null });
  },

  /** Open a draft from another tab (Library). If Compose is already
   *  initialized, switch to it now; otherwise stash it for init(). */
  openDraft: (id) => {
    if (get().sub) void get().selectDraft(id);
    else set({ requestedId: id });
  },

  setTitle: (title) => {
    const { activeId, sub, drafts } = get();
    if (!activeId || !sub) return;
    void updateDraft(activeId, { title });
    set({ drafts: drafts.map((d) => d.id === activeId ? { ...d, title } : d) });
    scheduleBackup(() => backupContext(sub, get().drafts), (ok) => set({ backup: ok ? "saved" : "error" }));
    set({ backup: "saving" });
  },

  setBody: (body) => {
    const { activeId, sub, drafts } = get();
    if (!activeId || !sub) return;
    void updateDraft(activeId, { body });
    set({ drafts: drafts.map((d) => d.id === activeId ? { ...d, body } : d) });
    scheduleBackup(() => backupContext(sub, get().drafts), (ok) => set({ backup: ok ? "saved" : "error" }));
    set({ backup: "saving" });
  },

  removeDraft: async (id) => {
    const sub = get().sub;
    if (!sub) return;
    await deleteDraft(id);
    let drafts = await listDrafts(sub);
    if (drafts.length === 0) {
      await createDraft(sub, { title: "", body: "" });
      drafts = await listDrafts(sub);
    }
    const activeId = get().activeId === id ? drafts[0].id : get().activeId;
    const messages = await listMessages(activeId!);
    set({ drafts, activeId, messages });
    scheduleBackup(() => backupContext(sub, get().drafts), (ok) => set({ backup: ok ? "saved" : "error" }));
  },

  insertIntoBody: (snippet) => {
    const { drafts, activeId } = get();
    const draft = drafts.find((d) => d.id === activeId);
    if (!draft) return;
    const sep = draft.body.endsWith("\n") || draft.body.length === 0 ? "" : "\n\n";
    get().setBody(draft.body + sep + snippet + "\n");
  },

  publishActive: async () => {
    const { activeId, sub, drafts } = get();
    const draft = drafts.find((d) => d.id === activeId);
    if (!draft || !sub || !activeId) return;
    if (!draft.title.trim()) { set({ publishState: "error", publishError: "Give your post a title before publishing." }); return; }
    if (!draft.body.trim()) { set({ publishState: "error", publishError: "The post is empty." }); return; }

    const slug = draft.published_slug ?? slugify(draft.title);
    if (await slugTaken(sub, slug, activeId)) {
      set({ publishState: "error", publishError: `Another published post already uses the slug "${slug}". Rename this one.` });
      return;
    }

    set({ publishState: "publishing", publishError: null, publishedUrl: null });
    try {
      // Fill a summary if the creator didn't write one, then graduate
      // the draft to a published post and render the whole site.
      if (!draft.summary.trim()) await updateDraft(activeId, { summary: autoSummary(draft.body) });
      await setPublished(activeId, slug, Date.now());
      const fresh = await listDrafts(sub);
      set({ drafts: fresh });

      const { siteUrl } = await publishAll(sub, `BKA: publish "${draft.title.trim()}"`);
      const postUrl = `${siteUrl}/posts/${slug}`;
      set({ publishState: "building", publishedUrl: postUrl });

      // If the creator's in discovery, hint the indexer to re-pull now
      // rather than wait for its cron. Best-effort; never blocks publish.
      const onb = await getOnboarding(sub);
      const credential = useAuthStore.getState().user?.credential;
      if (onb?.indexer_registered && credential) void pingRefresh(credential);

      // Poll the live URL (no-cors) until the CF Pages rebuild serves it.
      // Bounded — we don't block forever if a build is slow.
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, i === 0 ? 4000 : 6000));
        if (get().publishState !== "building" || get().publishedUrl !== postUrl) return; // superseded
        try {
          await fetch(postUrl, { mode: "no-cors", cache: "no-store" });
          set({ publishState: "live" });
          return;
        } catch { /* not up yet */ }
      }
      // Gave up waiting — it's committed and will appear once CF finishes.
      if (get().publishState === "building") set({ publishState: "live" });
    } catch (e) {
      set({ publishState: "error", publishError: e instanceof Error ? e.message : String(e) });
    }
  },

  unpublish: async (id) => {
    const sub = get().sub;
    if (!sub) return;
    set({ publishState: "publishing", publishError: null });
    try {
      await clearPublished(id);
      set({ drafts: await listDrafts(sub) });
      await publishAll(sub, "BKA: unpublish a post");
      set({ publishState: "idle", publishedUrl: null });
    } catch (e) {
      set({ publishState: "error", publishError: e instanceof Error ? e.message : String(e) });
    }
  },

  send: async (userText) => {
    const text = userText.trim();
    const { activeId, sub, messages } = get();
    if (!text || !activeId || !sub || get().streaming) return;

    const ks = useKeyStore.getState();
    const provider = ks.provider;
    const token = ks.activeToken();
    const model = ks.activeModel();
    if (!token) {
      set({ error: provider === "anthropic"
        ? "Add your Anthropic API key (top right) to chat."
        : "Bundle of Babies is unavailable — no access token configured." });
      return;
    }

    const draft = await getDraft(activeId);
    if (!draft) return;

    // Persist + show the user's message.
    const userMsg = await addMessage(activeId, "user", text);
    set({ messages: [...messages, userMsg], streaming: true, streamingText: "", error: null });

    const history: ChatTurnMessage[] = [...messages, userMsg].map((m) => ({ role: m.role, content: m.content }));

    const filter = createEditStreamFilter({
      onText: (delta) => set((s) => ({ streamingText: s.streamingText + delta })),
      onEditStart: () => set({ aiEditing: true }),
      onEdit: (content) => { get().setBody(content); },
      onEditEnd: () => set({ aiEditing: false }),
      onCreateDocument: (payload) => {
        // Materialize a new draft and switch to it.
        void (async () => {
          const d = await createDraft(sub, { title: payload.name, body: payload.content });
          const drafts = await listDrafts(sub);
          set({ drafts, activeId: d.id, messages: [] });
          get().setBody(payload.content);
        })();
      },
    });

    try {
      const result = await streamChat({
        provider, token, model, history,
        onToken: (delta) => filter.feed(delta),
        systemPrompt: buildSystemPrompt(draft),
      });
      filter.end();
      // Persist the chat-visible prose (tags already stripped by the
      // filter via streamingText); fall back to the raw result if the
      // model emitted only tags.
      const visible = get().streamingText.trim() || "(updated your draft)";
      void result;
      const aiMsg = await addMessage(activeId, "assistant", visible);
      set((s) => ({ messages: [...s.messages.filter((m) => m.draft_id === activeId), aiMsg], streaming: false, streamingText: "", aiEditing: false }));
    } catch (e) {
      filter.end();
      set({ streaming: false, aiEditing: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  backupNow: async () => {
    const sub = get().sub;
    if (!sub) return;
    flushBackup();
    const ctx = await backupContext(sub, get().drafts);
    if (!ctx) { set({ error: "Can't back up yet — finish onboarding first." }); return; }
    set({ backup: "saving" });
    try {
      await backupDrafts(ctx.token, ctx.privateRepo, ctx.drafts);
      set({ backup: "saved" });
    } catch (e) {
      set({ backup: "error", error: e instanceof Error ? e.message : String(e) });
    }
  },

  clearError: () => set({ error: null }),
}));
